import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";

// Terms of Service — Sprint 2 Batch 1 (May 2026).
// Pure static page. NO backend, NO database. Reasonable starter terms for a
// childcare-tax SaaS. Review with counsel before broad public launch.
export default function Terms() {
  const effectiveDate = "May 21, 2026";
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-sm text-[#2563EB] hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-700" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Terms of Service</h1>
              <p className="text-xs text-slate-500 mt-1">Effective {effectiveDate}</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none text-sm leading-relaxed space-y-6" data-testid="terms-content">
            <p>
              These Terms govern your use of Today's Kidz Simple Sign In Tax ("the Service"),
              operated by Today's Kidz Childcare Center ("we", "us"). By creating an account or
              using the Service, you agree to these Terms.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">1. The Service</h2>
            <p>
              Simple Sign In Tax helps US-based childcare providers organize bank statements,
              receipts, CACFP records, and payroll data; auto-categorize transactions into IRS
              Schedule C buckets; track equipment depreciation and Section 179 elections; and
              produce reports a tax preparer can use. <strong>We are not a CPA, an enrolled agent,
              or a tax-preparation service.</strong> We do not file your tax return for you. You
              are solely responsible for the accuracy of your tax filings.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">2. Who can use it</h2>
            <p>
              You must be at least 18 years old, a US resident, and operating a licensed US
              childcare facility (or be authorized to file taxes on behalf of one). You agree to
              provide accurate registration information and to keep your account credentials
              confidential.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">3. Subscription, billing, refunds</h2>
            <p>
              The Service is sold as <strong>pay-per-tax-year, NOT recurring monthly subscription</strong>.
              When you pay for Basic ($99), Premium ($149), or Premium Plus ($249), you receive
              access to the Service for one specific filing year (typically the prior calendar year).
              Pages used by your tier are tracked per filing year and reset only when you pay again
              for a different tax year.
            </p>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li>Payments are processed by Square. We do not store credit card data.</li>
              <li>One free trial of 15 pages is offered per email address per filing year. Repeat trial signups under the same email for the same tax year are not permitted.</li>
              <li><strong>Refunds:</strong> we offer a 7-day refund for any reason from the original purchase date. Refunds requested after 7 days are at our sole discretion, generally granted only in cases of clear technical failure that prevented you from using the Service. Email <a href="mailto:admin@todayskidzsimplesignin.com" className="text-[#2563EB] font-semibold hover:underline">admin@todayskidzsimplesignin.com</a> to request a refund.</li>
              <li><strong>Page top-up packs</strong> ($14.99 / $49.99 / $129) are non-refundable once used (any page count consumed).</li>
            </ul>

            <h2 className="text-lg font-bold text-slate-900 mt-6">4. Your data, your responsibility</h2>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li>You retain full ownership of every document you upload and every transaction you create. We hold a limited license to process and display this data to you, and to back it up in encrypted storage.</li>
              <li>You are responsible for the accuracy of the data you upload and the categorizations you approve. Our AI categorization is best-effort; you must review and approve each transaction before it appears in your tax reports.</li>
              <li>You are responsible for verifying every tax figure with a licensed tax professional before filing.</li>
              <li>We retain backups for operational durability. When you delete your account, your data is permanently deleted within 7 days of confirmation (the grace period for restoration).</li>
            </ul>

            <h2 className="text-lg font-bold text-slate-900 mt-6">5. Acceptable use</h2>
            <p>You agree NOT to:</p>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li>Use the Service to organize tax records of an entity you don't represent or own.</li>
              <li>Upload files containing material unrelated to legitimate childcare-business records (e.g., illegal content, malware, copyrighted material you don't own).</li>
              <li>Attempt to bypass quota limits, trial enforcement, or paywalls (e.g., creating multiple accounts to extend a free trial).</li>
              <li>Scrape, reverse-engineer, or attempt to gain unauthorized access to other accounts.</li>
              <li>Resell or sublicense the Service to third parties without a written sponsorship agreement with us.</li>
            </ul>
            <p>Violation can result in immediate account suspension without refund.</p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">6. AI and OCR caveats</h2>
            <p>
              Bank statement and receipt parsing uses Google's Gemini model. AI categorization is
              probabilistic; it can mis-read amounts, dates, or categories. <strong>You are
              required to review and approve every transaction before it appears in any tax
              report.</strong> Approved transactions are the only ones that count toward your
              Schedule C, CACFP allocation, or Form 4562. We are not liable for tax errors arising
              from un-reviewed AI output.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">7. Compliance — the tax disclaimer you saw on signup</h2>
            <p>
              The Service is a record-organization tool, not tax advice. The reports we generate
              (Schedule C summary, Form 4562 Part I and Part V, CACFP allocation worksheets) are
              <strong> not filed with the IRS, USDA, or any state agency by us.</strong> You must
              review them with a licensed tax professional, and you alone are responsible for what
              you file.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">8. Service availability</h2>
            <p>
              We aim for high uptime but make no guarantee of uninterrupted service. Scheduled
              maintenance, third-party outages (Square, Resend, Cloudflare, the database), and
              unforeseen incidents may temporarily make the Service unavailable. We will not be
              liable for losses caused by temporary unavailability.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">9. Account termination</h2>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li>You can delete your account at any time from Settings.</li>
              <li>We may suspend or terminate your account for violations of these Terms with reasonable notice (or immediate, in cases of suspected fraud, abuse, or security risk).</li>
              <li>On termination, you lose access to the Service but you can export your data first via the "Export my data" feature.</li>
            </ul>

            <h2 className="text-lg font-bold text-slate-900 mt-6">10. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, our total liability for any claim arising
              from your use of the Service is limited to the amount you paid us in the 12 months
              preceding the claim. We are not liable for indirect, incidental, consequential, or
              punitive damages, including but not limited to lost profits, tax penalties, audit
              costs, or lost data — even if we were advised of the possibility.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold us harmless from any claim arising out of (a) your
              use of the Service, (b) your violation of these Terms, (c) your violation of any
              third-party right, or (d) the accuracy or legality of the tax records you upload or
              the filings you submit based on our reports.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">12. Governing law and dispute resolution</h2>
            <p>
              These Terms are governed by the laws of the State of Ohio, USA, without regard to
              conflict-of-law principles. Any dispute will first be addressed by good-faith email
              negotiation. If unresolved within 30 days, disputes will be submitted to binding
              arbitration administered by the American Arbitration Association under its consumer
              arbitration rules, conducted in Ohio. <strong>You and we both waive the right to a
              jury trial and to participate in any class-action lawsuit.</strong>
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">13. Changes to these terms</h2>
            <p>
              We may revise these Terms over time. We will notify active users by email at least 30
              days before material changes take effect. Continued use of the Service after the
              effective date constitutes acceptance of the revised Terms.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">14. Contact</h2>
            <p>
              For Terms-related questions or to report concerns:{" "}
              <a href="mailto:admin@todayskidzsimplesignin.com" className="text-[#2563EB] font-semibold hover:underline">
                admin@todayskidzsimplesignin.com
              </a>.
            </p>

            <p className="text-xs text-slate-400 italic pt-6 border-t border-slate-200 mt-8">
              These Terms of Service are provided as a reasonable starting point for a US-based
              tax-records SaaS. They are not legal advice. We recommend you have them reviewed by
              an attorney before relying on them in commercial deployment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
